import { useState, useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api, formatApiErrorDetail } from "@/api";
import { PRIORITIES, VEHICLE_STATUSES, rangeFor, fmtRangeLabel, fmtMinutes, fmtTime } from "@/lib/constants";
import { FileBarChart, Printer, Loader2, ShieldCheck, ClipboardList, Truck, Timer, Droplet, Waves, FileText, GraduationCap } from "lucide-react";
import { MapShell } from "@/components/MapShell";
import { LogoMark } from "@/context/BrandingContext";
import { toast } from "sonner";

function ReportMap({ points, area }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const map = L.map(el.current, { zoomControl: true, attributionControl: false }).setView([52.11, 5.18], 13);
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    const layers = [];
    if (area && area.length >= 3) {
      const poly = L.polygon(area, { color: "#2563eb", weight: 2, fillColor: "#2563eb", fillOpacity: 0.08 });
      poly.addTo(map); layers.push(poly);
    }
    (points || []).forEach((p) => {
      const hex = PRIORITIES[p.priority]?.hex || "#64748b";
      const icon = L.divIcon({ className: "", html: `<div style="width:16px;height:16px;border-radius:50%;background:${hex};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`, iconAnchor: [8, 8] });
      const m = L.marker([p.lat, p.lng], { icon }).addTo(map).bindPopup(`<b>${p.ref}</b> · P${p.priority}<br/>${p.title}<br/><i>${p.building}</i>`);
      layers.push(m);
    });
    if (layers.length) { try { map.fitBounds(L.featureGroup(layers).getBounds().pad(0.3)); } catch (e) {} }
    setTimeout(() => map.invalidateSize(), 250);
  }, [points, area]);
  return (
    <MapShell heightClass="h-80" testid="report-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
      <div ref={el} data-testid="report-map" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
    </MapShell>
  );
}

function WaterReportMap({ points, buildings, area, coverage }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const covRef = useRef(null);
  const [showCov, setShowCov] = useState(true);
  const radiusOf = (w) => w.coverage_m || (coverage && coverage[w.type]) || 200;
  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const map = L.map(el.current, { zoomControl: true, attributionControl: false }).setView([52.11, 5.18], 13);
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    const layers = [];
    if (area && area.length >= 3) { const p = L.polygon(area, { color: "#2563eb", weight: 2, fillColor: "#2563eb", fillOpacity: 0.06 }); p.addTo(map); layers.push(p); }
    (buildings || []).forEach((b) => {
      if (b.lat == null) return;
      const ic = L.divIcon({ className: "", html: `<div style="background:#1e40af;color:#fff;border:2px solid #fff;border-radius:8px;padding:2px 6px;font-size:10px;font-weight:700;white-space:nowrap">${b.name}</div>`, iconAnchor: [14, 10] });
      layers.push(L.marker([b.lat, b.lng], { icon: ic }).addTo(map));
    });
    const cov = L.layerGroup();
    (points || []).forEach((w) => {
      if (w.lat == null) return;
      const col = w.type === "Blusvijver" ? "#0d9488" : "#0ea5e9";
      const ic = L.divIcon({ className: "", html: `<div style="width:14px;height:14px;border-radius:50% 50% 50% 0;background:${col};border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`, iconAnchor: [7, 12] });
      layers.push(L.marker([w.lat, w.lng], { icon: ic }).addTo(map).bindPopup(`${w.type === "Blusvijver" ? "🌊" : "💧"} ${w.name}${w.capacity ? " · " + w.capacity + " l/min" : ""} · dekking ${radiusOf(w)} m`));
      const c = L.circle([w.lat, w.lng], { radius: radiusOf(w), color: col, weight: 1, fillColor: col, fillOpacity: 0.12 });
      cov.addLayer(c); layers.push(c);
    });
    covRef.current = cov;
    if (showCov) cov.addTo(map);
    if (layers.length) { try { map.fitBounds(L.featureGroup(layers).getBounds().pad(0.2)); } catch (e) {} }
    setTimeout(() => map.invalidateSize(), 250);
  }, [points, buildings, area, coverage]); // eslint-disable-line
  useEffect(() => {
    const map = mapRef.current, cov = covRef.current; if (!map || !cov) return;
    if (showCov) cov.addTo(map); else map.removeLayer(cov);
  }, [showCov]);
  return (
    <>
      <label className="mb-2 inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-600" data-testid="coverage-toggle">
        <input type="checkbox" checked={showCov} onChange={(e) => setShowCov(e.target.checked)} className="h-4 w-4 accent-sky-600" />
        Dekkingscirkels tonen (straal per bluswaterpunt)
      </label>
      <MapShell heightClass="h-80" testid="readiness-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
        <div ref={el} data-testid="readiness-map" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
      </MapShell>
    </>
  );
}

const MODES = [
  { key: "dienst", label: "Dienst (vandaag)" },
  { key: "week", label: "Deze week" },
  { key: "maand", label: "Deze maand" },
  { key: "custom", label: "Van–tot" },
];

const REPORT_TYPES = [
  { key: "period", label: "Periodiek totaaloverzicht", desc: "Incidenten, statistieken, kaart & AI-samenvatting", icon: FileBarChart, range: true },
  { key: "handover", label: "Dienstoverdracht", desc: "Openstaande incidenten, voertuigstatus & laatste kladblok", icon: ClipboardList, range: false },
  { key: "vehicles", label: "Voertuiginzet", desc: "Inzetten & statuswijzigingen per voertuig", icon: Truck, range: true },
  { key: "response", label: "Responstijden & prestaties", desc: "Respons-/afhandeltijd per prioriteit + uitschieters", icon: Timer, range: true },
  { key: "readiness", label: "Paraatheid: bluswater & locaties", desc: "Bluswaterpunten & gebouwen met aandachtskaart/kenmerken", icon: ShieldCheck, range: false },
  { key: "learning", label: "Leerdoelen (overkoepelend)", desc: "Bundelt evaluaties & leerdoelen van gesloten incidenten met AI-analyse", icon: GraduationCap, range: true },
];
const RANGE_EP = { period: "/reports/period", vehicles: "/reports/vehicles", response: "/reports/response-times", learning: "/reports/learning-goals" };
const SNAP_EP = { handover: "/reports/handover", readiness: "/reports/readiness" };

function StatTile({ l, v }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{l}</div>
      <div className="mt-1 font-display text-2xl font-bold text-slate-900">{v}</div>
    </div>
  );
}

function ReportHeader({ title, subtitle, report }) {
  return (
    <div className="flex items-start justify-between border-b border-slate-200 pb-5">
      <div className="flex items-center gap-3">
        <LogoMark size={40} rounded="rounded-xl" />
        <div>
          <div className="font-display text-lg font-bold text-slate-900">DispatchMe — {title}</div>
          <div className="text-sm text-slate-500">{subtitle}</div>
        </div>
      </div>
      <div className="text-right text-xs text-slate-400">
        <div>Gegenereerd: {fmtTime(report.generated_at)}</div>
        <div>Door: {report.generated_by}</div>
      </div>
    </div>
  );
}

function AiSummary({ text }) {
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-5 print-break">
      <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-900"><FileBarChart className="h-4 w-4 text-blue-600" /> Samenvatting (AI)</div>
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700" data-testid="report-ai-summary">{text}</div>
    </div>
  );
}

function VehStatusBadge({ status }) {
  const st = VEHICLE_STATUSES[status] || { label: status, bg: "#F1F5F9", text: "#334155" };
  return <span className="rounded px-2 py-0.5 text-[11px] font-semibold" style={{ backgroundColor: st.bg, color: st.text }}>{st.label}</span>;
}

function PeriodReport({ report, subtitle }) {
  const s = report.stats;
  return (
    <>
      <ReportHeader icon={ShieldCheck} title="Periodieke rapportage" subtitle={subtitle} report={report} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 print-break">
        <StatTile l="Totaal incidenten" v={s.total} />
        <StatTile l="Gesloten" v={s.closed} />
        <StatTile l="Nog open" v={s.open} />
        <StatTile l="Kladblok-notities" v={s.notes_count} />
        <StatTile l="Gem. tijd-tot-sluiten" v={fmtMinutes(s.avg_close_min)} />
        <StatTile l="Gem. responstijd" v={fmtMinutes(s.avg_response_min)} />
      </div>
      <div className="grid gap-4 sm:grid-cols-3 print-break">
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="mb-2 text-sm font-bold text-slate-900">Per type</div>
          {Object.keys(s.by_type).length === 0 ? <div className="text-sm text-slate-400">Geen data</div> :
            Object.entries(s.by_type).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-1 text-sm"><span className="text-slate-600">{k}</span><span className="font-semibold text-slate-900">{v}</span></div>
            ))}
        </div>
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="mb-2 text-sm font-bold text-slate-900">Per prioriteit</div>
          {[1, 2, 3, 4].map((p) => (
            <div key={p} className="flex items-center justify-between py-1 text-sm"><span style={{ color: PRIORITIES[p].text }} className="font-medium">{PRIORITIES[p].label} · P{p}</span><span className="font-semibold text-slate-900">{s.by_priority[p] || 0}</span></div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="mb-2 text-sm font-bold text-slate-900">Per gebouw</div>
          {Object.keys(s.by_building || {}).length === 0 ? <div className="text-sm text-slate-400">Geen data</div> :
            Object.entries(s.by_building).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-1 text-sm"><span className="truncate text-slate-600">{k}</span><span className="ml-2 font-semibold text-slate-900">{v}</span></div>
            ))}
        </div>
      </div>
      {(report.map_points?.length > 0 || report.service_area) && (
        <div className="print-break">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-bold text-slate-900">Incidenten & risicozone op kaart</div>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              {[1, 2, 3, 4].map((p) => (<span key={p} className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PRIORITIES[p].hex }} /> {PRIORITIES[p].label}</span>))}
            </div>
          </div>
          <ReportMap points={report.map_points || []} area={report.service_area} />
        </div>
      )}
      <AiSummary text={report.ai_summary} />
      <div className="print-break">
        <div className="mb-2 text-sm font-bold text-slate-900">Incidenten in periode ({report.incidents.length})</div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Ref</th><th className="py-2">P</th><th className="py-2">Titel</th><th className="py-2">Type</th><th className="py-2">Gebouw</th><th className="py-2">Status</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {report.incidents.map((i) => (
              <tr key={i.ref}>
                <td className="py-2 font-mono text-xs text-slate-500">{i.ref}</td>
                <td className="py-2"><span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: PRIORITIES[i.priority].bg, color: PRIORITIES[i.priority].text }}>P{i.priority}</span></td>
                <td className="py-2 font-medium text-slate-900">{i.title}</td>
                <td className="py-2 text-slate-600">{i.type}</td>
                <td className="py-2 text-slate-600">{i.building}</td>
                <td className="py-2 text-slate-500">{i.status === "closed" ? "Gesloten" : "Open"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function HandoverReport({ report }) {
  return (
    <>
      <ReportHeader icon={ClipboardList} title="Dienstoverdracht" subtitle="Momentopname voor ploegwissel" report={report} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 print-break">
        <StatTile l="Openstaande incidenten" v={report.open_incidents.length} />
        <StatTile l="Beschikbare voertuigen" v={report.vehicles.filter((v) => v.status === "beschikbaar").length} />
        <StatTile l="Voertuigen in inzet" v={report.vehicles.filter((v) => ["uitgerukt", "ter_plaatse"].includes(v.status)).length} />
        <StatTile l="Recente kladblokregels" v={report.recent_notes.length} />
      </div>
      <AiSummary text={report.ai_summary} />
      <div className="print-break">
        <div className="mb-2 text-sm font-bold text-slate-900">Openstaande incidenten</div>
        {report.open_incidents.length === 0 ? <div className="text-sm text-slate-400">Geen openstaande incidenten</div> : (
          <table className="w-full text-sm"><thead className="text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Ref</th><th className="py-2">P</th><th className="py-2">Titel</th><th className="py-2">Gebouw</th><th className="py-2">Voertuigen</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {report.open_incidents.map((i) => (
                <tr key={i.ref}>
                  <td className="py-2 font-mono text-xs text-slate-500">{i.ref}</td>
                  <td className="py-2"><span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: PRIORITIES[i.priority].bg, color: PRIORITIES[i.priority].text }}>P{i.priority}</span></td>
                  <td className="py-2 font-medium text-slate-900">{i.title}</td>
                  <td className="py-2 text-slate-600">{i.building}</td>
                  <td className="py-2 text-slate-500">{i.vehicles.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody></table>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 print-break">
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="mb-2 text-sm font-bold text-slate-900">Voertuigstatus</div>
          {report.vehicles.map((v) => (
            <div key={v.callsign} className="flex items-center justify-between py-1.5 text-sm">
              <span className="font-mono font-semibold text-slate-900">{v.callsign} <span className="font-sans font-normal text-slate-400">· {v.name}</span></span>
              <VehStatusBadge status={v.status} />
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="mb-2 text-sm font-bold text-slate-900">Laatste kladblokregels</div>
          {report.recent_notes.length === 0 ? <div className="text-sm text-slate-400">Geen notities</div> :
            report.recent_notes.map((n, idx) => (
              <div key={idx} className="border-b border-slate-100 py-1.5 text-sm last:border-0">
                <div className="text-xs text-slate-400"><span className="font-mono">{n.ref}</span> · {n.author} · {fmtTime(n.created_at)}</div>
                <div className="text-slate-700">{n.text}</div>
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

function VehiclesReport({ report, subtitle }) {
  return (
    <>
      <ReportHeader icon={Truck} title="Voertuiginzet-rapportage" subtitle={subtitle} report={report} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 print-break">
        <StatTile l="Totaal inzetten" v={report.total_deployments} />
        <StatTile l="Voertuigen" v={report.vehicles.length} />
        <StatTile l="Actief nu" v={report.vehicles.filter((v) => ["uitgerukt", "ter_plaatse"].includes(v.current_status)).length} />
      </div>
      <AiSummary text={report.ai_summary} />
      <div className="print-break">
        <div className="mb-2 text-sm font-bold text-slate-900">Per voertuig</div>
        <table className="w-full text-sm"><thead className="text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Roepnaam</th><th className="py-2">Naam</th><th className="py-2">Inzetten</th><th className="py-2">Statuswijzigingen</th><th className="py-2">Huidige status</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {report.vehicles.map((v) => (
              <tr key={v.callsign}>
                <td className="py-2 font-mono font-semibold text-slate-900">{v.callsign}</td>
                <td className="py-2 text-slate-600">{v.name}</td>
                <td className="py-2 font-semibold text-slate-900">{v.deployments}</td>
                <td className="py-2 text-slate-600">{v.status_changes}</td>
                <td className="py-2"><VehStatusBadge status={v.current_status} /></td>
              </tr>
            ))}
          </tbody></table>
      </div>
    </>
  );
}

function ResponseReport({ report, subtitle }) {
  const s = report.stats;
  return (
    <>
      <ReportHeader icon={Timer} title="Responstijden & prestaties" subtitle={subtitle} report={report} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 print-break">
        <StatTile l="Totaal incidenten" v={s.total} />
        <StatTile l="Gem. responstijd" v={fmtMinutes(s.avg_response_min)} />
        <StatTile l="Gem. afhandeltijd" v={fmtMinutes(s.avg_close_min)} />
      </div>
      <div className="print-break">
        <div className="mb-2 text-sm font-bold text-slate-900">Per prioriteit</div>
        <table className="w-full text-sm"><thead className="text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Prioriteit</th><th className="py-2">Aantal</th><th className="py-2">Gem. respons</th><th className="py-2">Gem. afhandeling</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {[1, 2, 3, 4].map((p) => {
              const r = report.by_priority[p] || {};
              return (
                <tr key={p}>
                  <td className="py-2"><span className="font-medium" style={{ color: PRIORITIES[p].text }}>{PRIORITIES[p].label} · P{p}</span></td>
                  <td className="py-2 text-slate-900">{r.count || 0}</td>
                  <td className="py-2 text-slate-600">{fmtMinutes(r.avg_response_min)}</td>
                  <td className="py-2 text-slate-600">{fmtMinutes(r.avg_close_min)}</td>
                </tr>
              );
            })}
          </tbody></table>
      </div>
      <AiSummary text={report.ai_summary} />
      <div className="print-break">
        <div className="mb-2 text-sm font-bold text-slate-900">Traagste afhandeling (top {report.slowest.length})</div>
        {report.slowest.length === 0 ? <div className="text-sm text-slate-400">Geen afgehandelde incidenten in periode</div> : (
          <table className="w-full text-sm"><thead className="text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Ref</th><th className="py-2">P</th><th className="py-2">Titel</th><th className="py-2">Gebouw</th><th className="py-2">Respons</th><th className="py-2">Afhandeling</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {report.slowest.map((i) => (
                <tr key={i.ref}>
                  <td className="py-2 font-mono text-xs text-slate-500">{i.ref}</td>
                  <td className="py-2"><span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: PRIORITIES[i.priority].bg, color: PRIORITIES[i.priority].text }}>P{i.priority}</span></td>
                  <td className="py-2 font-medium text-slate-900">{i.title}</td>
                  <td className="py-2 text-slate-600">{i.building}</td>
                  <td className="py-2 text-slate-600">{fmtMinutes(i.response_min)}</td>
                  <td className="py-2 font-semibold text-slate-900">{fmtMinutes(i.close_min)}</td>
                </tr>
              ))}
            </tbody></table>
        )}
      </div>
    </>
  );
}

function ReadinessReport({ report }) {
  return (
    <>
      <ReportHeader icon={ShieldCheck} title="Paraatheid: bluswater & locaties" subtitle="Momentopname masterdata" report={report} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 print-break">
        <StatTile l="Brandkranen" v={report.water_summary.Brandkraan || 0} />
        <StatTile l="Blusvijvers" v={report.water_summary.Blusvijver || 0} />
        <StatTile l="Gebouwen totaal" v={report.buildings_total} />
        <StatTile l="Met aandachtskaart/kenmerken" v={report.buildings_with_card} />
      </div>
      <AiSummary text={report.ai_summary} />
      {(report.water_map?.length > 0 || report.building_points?.length > 0) && (
        <div className="print-break">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-bold text-slate-900">Bluswater &amp; gebouwen op kaart</div>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#0ea5e9" }} /> Brandkraan</span>
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#0d9488" }} /> Blusvijver</span>
            </div>
          </div>
          <WaterReportMap points={report.water_map || []} buildings={report.building_points || []} area={report.service_area} coverage={report.water_coverage} />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 print-break">
        {Object.entries(report.water_points).map(([type, list]) => (
          <div key={type} className="rounded-xl border border-slate-200 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-900">
              {type === "Blusvijver" ? <Waves className="h-4 w-4 text-teal-600" /> : <Droplet className="h-4 w-4 text-sky-600" />} {type} ({list.length})
            </div>
            {list.length === 0 ? <div className="text-sm text-slate-400">Geen</div> :
              list.map((w, i) => (<div key={i} className="flex items-center justify-between py-1 text-sm"><span className="text-slate-700">{w.name}</span>{w.note && <span className="text-xs text-slate-400">{w.note}</span>}</div>))}
          </div>
        ))}
      </div>
      <div className="print-break">
        <div className="mb-2 text-sm font-bold text-slate-900">Gebouwen & aandachtspunten</div>
        <table className="w-full text-sm"><thead className="text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Gebouw</th><th className="py-2">Functie</th><th className="py-2">Kenmerken</th><th className="py-2">Aandachtskaart</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {report.buildings.map((b, idx) => (
              <tr key={`${b.code || b.name}-${idx}`}>
                <td className="py-2 font-medium text-slate-900">{b.name} <span className="font-mono text-xs text-slate-400">{b.code}</span></td>
                <td className="py-2 text-slate-600">{b.function || "—"}</td>
                <td className="py-2">
                  <div className="flex flex-wrap gap-1">{(b.kenmerken || []).map((k, i) => <span key={i} className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">{k}</span>)}{(b.kenmerken || []).length === 0 && <span className="text-xs text-slate-400">—</span>}</div>
                </td>
                <td className="py-2">{b.has_card ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600"><FileText className="h-3.5 w-3.5" /> ja</span> : <span className="text-xs text-slate-400">nee</span>}</td>
              </tr>
            ))}
          </tbody></table>
      </div>
    </>
  );
}

function LearningReport({ report, subtitle }) {
  return (
    <>
      <ReportHeader icon={GraduationCap} title="Overkoepelend leerdoelen-rapport" subtitle={subtitle} report={report} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 print-break">
        <StatTile l="Geëvalueerde incidenten" v={report.count} />
      </div>
      <AiSummary text={report.ai_summary} />
      <div className="print-break">
        <div className="mb-2 text-sm font-bold text-slate-900">Evaluaties per incident ({report.incidents.length})</div>
        {report.incidents.length === 0 ? (
          <div className="text-sm text-slate-400">Geen afgesloten incidenten met evaluatie in deze selectie.</div>
        ) : (
          <div className="space-y-3">
            {report.incidents.map((i) => (
              <div key={i.ref} className="rounded-xl border border-slate-200 p-4">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: PRIORITIES[i.priority].bg, color: PRIORITIES[i.priority].text }}>P{i.priority}</span>
                  <span className="font-mono text-xs text-slate-400">{i.ref}</span>
                  <span className="text-sm font-semibold text-slate-900">{i.title}</span>
                  <span className="text-xs text-slate-400">· {i.type}{i.building ? ` · ${i.building}` : ""}{i.closed_at ? ` · ${fmtTime(i.closed_at)}` : ""}</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg bg-emerald-50 p-3">
                    <div className="mb-1 text-xs font-bold uppercase tracking-wide text-emerald-700">Wat ging goed</div>
                    <div className="whitespace-pre-wrap text-sm text-slate-700">{i.went_well || "—"}</div>
                  </div>
                  <div className="rounded-lg bg-amber-50 p-3">
                    <div className="mb-1 text-xs font-bold uppercase tracking-wide text-amber-700">Leerdoelen</div>
                    <div className="whitespace-pre-wrap text-sm text-slate-700">{i.learning_goals || "—"}</div>
                  </div>
                </div>
                {i.by && <div className="mt-2 text-xs text-slate-400">Geëvalueerd door {i.by}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default function Reports() {
  const [shift, setShift] = useState({ start: "08:00", end: "08:00" });
  const [type, setType] = useState("period");
  const [mode, setMode] = useState("maand");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [learnAllTime, setLearnAllTime] = useState(true);

  useEffect(() => { api.get("/settings/shift").then(({ data }) => setShift(data)); }, []);

  const rt = REPORT_TYPES.find((r) => r.key === type);
  const usesRange = rt.range;

  const selectType = (k) => { setType(k); setReport(null); };

  const generate = async () => {
    setLoading(true); setReport(null);
    try {
      let data;
      if (usesRange) {
        if (type === "learning" && learnAllTime) {
          ({ data } = await api.post(RANGE_EP[type], { start: null, end: null }));
        } else {
          const [a, b] = rangeFor(mode, shift, custom);
          ({ data } = await api.post(RANGE_EP[type], { start: a.toISOString(), end: b.toISOString() }));
        }
      } else {
        ({ data } = await api.get(SNAP_EP[type]));
      }
      setReport({ ...data, _kind: type });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const rangeReady = (type === "learning" && learnAllTime) || mode !== "custom" || (custom.from && custom.to);
  const subtitle = usesRange ? ((type === "learning" && learnAllTime) ? "Alle gesloten incidenten" : fmtRangeLabel(mode, shift, custom)) : `Gegenereerd ${fmtTime(new Date().toISOString())}`;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="no-print flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Rapportage</h1>
          <p className="text-sm text-slate-500">Kies een rapporttype, genereer en exporteer naar PDF</p>
        </div>
        {report && (
          <button data-testid="print-report" onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
            <Printer className="h-4 w-4" /> Afdrukken / PDF
          </button>
        )}
      </div>

      {/* Report type selector */}
      <div className="no-print grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {REPORT_TYPES.map((r) => {
          const active = type === r.key;
          return (
            <button key={r.key} data-testid={`report-type-${r.key}`} onClick={() => selectType(r.key)}
              className={`flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-all ${active ? "border-blue-500 bg-blue-50 shadow-sm ring-1 ring-blue-500" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"}`}>
              <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}><r.icon className="h-5 w-5" /></div>
              <div className="text-sm font-bold text-slate-900">{r.label}</div>
              <div className="text-xs text-slate-500">{r.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div className="no-print flex flex-wrap items-center gap-2">
        {type === "learning" && (
          <label data-testid="learning-alltime-toggle" className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600">
            <input type="checkbox" checked={learnAllTime} onChange={(e) => setLearnAllTime(e.target.checked)} className="h-4 w-4 accent-blue-600" />
            Alle gesloten incidenten (negeer periode)
          </label>
        )}
        {usesRange && !(type === "learning" && learnAllTime) && MODES.map((m) => (
          <button key={m.key} data-testid={`report-range-${m.key}`} onClick={() => setMode(m.key)}
            className={`rounded-xl px-3.5 py-2 text-sm font-medium transition-colors ${mode === m.key ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {m.label}
          </button>
        ))}
        {usesRange && !(type === "learning" && learnAllTime) && mode === "custom" && (
          <div className="flex items-center gap-2">
            <input data-testid="report-from" type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            <span className="text-slate-400">t/m</span>
            <input data-testid="report-to" type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          </div>
        )}
        <button data-testid="generate-report" onClick={generate} disabled={loading || !rangeReady}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <rt.icon className="h-4 w-4" />} {rt.label} genereren
        </button>
      </div>

      {loading && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-600" /> Rapport wordt opgesteld…
        </div>
      )}

      {report && (
        <div className="print-page mx-auto max-w-4xl space-y-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="report-output">
          {report._kind === "period" && <PeriodReport report={report} subtitle={subtitle} />}
          {report._kind === "handover" && <HandoverReport report={report} />}
          {report._kind === "vehicles" && <VehiclesReport report={report} subtitle={subtitle} />}
          {report._kind === "response" && <ResponseReport report={report} subtitle={subtitle} />}
          {report._kind === "readiness" && <ReadinessReport report={report} />}
          {report._kind === "learning" && <LearningReport report={report} subtitle={subtitle} />}
        </div>
      )}
    </div>
  );
}
